import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import Helper from "../Helper/helper";
import api from "../../Environment";
import { withToastManager } from "react-toast-notifications";
import ToastDemo from "../Helper/toaster";

import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from "react-responsive-carousel";

import { translate } from "react-multi-lang";
import configuration from "react-global-configuration";
import BrandAssetManager from "../../branding/BrandAssetManager";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import {
  fetchPlacement,
  trackAdClick,
  trackAdImpression,
} from "../Advertising/advertisingApi";
import DisplayAd from "../Advertising/DisplayAd";
import {
  DISPLAY_AD_SIZE_ATTRIBUTES,
  readDisplayAdSize,
} from "../Advertising/displayAdSize";
import {
  isDisplayNetworkItem,
  selectDisplayUnit,
  detectDeviceClass,
} from "../Advertising/providers/AdProviderResolver";
import { placementBackgroundUrl } from "../Advertising/placementBackground";
import "../Advertising/AdPlacement.css";
import "./homePageBanner.css";

/**
 * Resolve homepage hero media for a banner slide.
 * Banner Video wins when assigned; Banner Image is the fallback.
 */
export function resolveHomeBannerHero(video) {
  video = video || {};
  const kind = String(video.banner_media_kind || "").toLowerCase();
  const clip = String(video.banner_video_url || "").trim();
  const poster = resolveMediaUrl(
    video.banner_image || video.default_image || video.mobile_banner_image
  );

  if ((kind === "video" || (!kind && clip)) && clip) {
    const lower = clip.toLowerCase();
    if (lower.indexOf("youtube.com") !== -1 || lower.indexOf("youtu.be") !== -1) {
      return { type: "youtube", url: clip, poster };
    }
    if (lower.indexOf("vimeo.com") !== -1) {
      return { type: "vimeo", url: clip, poster };
    }
    if (lower.indexOf(".m3u8") !== -1) {
      return { type: "hls", url: clip, poster };
    }
    return { type: "video", url: resolveMediaUrl(clip, clip), poster };
  }

  return { type: "image", url: poster, poster };
}

function youtubeEmbedUrl(url) {
  try {
    const u = String(url || "");
    let id = "";
    if (u.indexOf("youtu.be/") !== -1) {
      id = u.split("youtu.be/")[1].split(/[?&]/)[0];
    } else if (u.indexOf("v=") !== -1) {
      id = u.split("v=")[1].split("&")[0];
    } else if (u.indexOf("/embed/") !== -1) {
      id = u.split("/embed/")[1].split(/[?&]/)[0];
    }
    if (!id) return null;
    return (
      "https://www.youtube.com/embed/" +
      id +
      "?autoplay=1&mute=1&controls=0&loop=1&playlist=" +
      id +
      "&playsinline=1&rel=0"
    );
  } catch (e) {
    return null;
  }
}

function vimeoEmbedUrl(url) {
  try {
    const match = String(url || "").match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (!match) return null;
    return (
      "https://player.vimeo.com/video/" +
      match[1] +
      "?autoplay=1&muted=1&loop=1&background=1"
    );
  } catch (e) {
    return null;
  }
}

/**
 * Upscale cap for provider creatives (1x bitmaps): enough to use spare slide
 * width on wide screens without visibly softening the creative.
 */
const PROVIDER_BANNER_MAX_SCALE = 1.25;

/**
 * Page Banner carousels (STANDARD_BANNER_FIT_KEYS) above the mobile breakpoint:
 * contained in the slide stage minus HOME_BANNER_FIT_GUTTER_X per side (clear of
 * the carousel arrow strips) and HOME_BANNER_FIT_PADDING_Y above and below the
 * label/creative group, enlarged at most HOME_BANNER_MAX_SCALE — never smaller
 * than the PROVIDER_BANNER_MAX_SCALE fit used everywhere else.
 */
const HOME_BANNER_FIT_KEY = "home_banner_slot";
const STANDARD_BANNER_FIT_KEYS = [
  HOME_BANNER_FIT_KEY,
  "series_banner_slot",
  "movies_banner_slot",
  "kids_banner_slot",
  "category_banner_slot",
];
const HOME_BANNER_FIT_MEDIA = "(min-width: 768px)";
const HOME_BANNER_FIT_PADDING_Y = 16;
const HOME_BANNER_FIT_GUTTER_X = 64;
// ≈ 2 × √1.5: at most 1.5× the area of the former 2× cap on very wide screens.
const HOME_BANNER_MAX_SCALE = 2.45;

/** Placement background for the provider slide — Home Banner only. */
export function homeBannerBackgroundUrl(banner, placementKey) {
  return placementKey === HOME_BANNER_FIT_KEY ? placementBackgroundUrl(banner) : null;
}

/**
 * Fits the provider slide's creative (its served size, else the DisplayAd-
 * selected unit — see readDisplayAdSize) inside the slide box minus the rest
 * of the centered label/creative group: aspect ratio kept, enlarged at most
 * PROVIDER_BANNER_MAX_SCALE (Page Banners on desktop: see HOME_BANNER_MAX_SCALE).
 */
function useProviderBannerFit(hostRef, placementKey) {
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
      return undefined;
    }
    const setVar = (prop, value) => {
      if (host.style.getPropertyValue(prop) !== value) host.style.setProperty(prop, value);
    };
    const sync = () => {
      const unit = host.querySelector(".display-ad[data-ad-width][data-ad-height]");
      const slot = unit ? unit.querySelector(".display-ad__slot") : null;
      const size = unit ? readDisplayAdSize(unit) : null;
      const unitWidth = size ? size.width : 0;
      const unitHeight = size ? size.height : 0;
      if (!slot || !(unitWidth > 0 && unitHeight > 0)) return;
      const hostBox = host.getBoundingClientRect();
      // Group height minus the scaled box: independent of where the group is centered.
      const group = slot.closest(".banner-provider-group") || host;
      const creative = slot.querySelector("ins.CANINSDisplayAD") || slot;
      const overhead = group.getBoundingClientRect().height - creative.getBoundingClientRect().height;
      const homeContain =
        STANDARD_BANNER_FIT_KEYS.indexOf(placementKey) !== -1 &&
        typeof window.matchMedia === "function" &&
        window.matchMedia(HOME_BANNER_FIT_MEDIA).matches;
      const availableHeight = hostBox.height - overhead;
      if (!(hostBox.width > 0 && availableHeight > 0)) return;
      // Rounded down so the zoomed creative never exceeds the fractional box.
      const fitScale = (width, height) =>
        Math.floor(Math.min(width / unitWidth, height / unitHeight) * 10000) / 10000;
      const headScale = Math.min(PROVIDER_BANNER_MAX_SCALE, fitScale(hostBox.width, availableHeight));
      const scale = homeContain
        ? Math.max(
            headScale,
            Math.min(
              HOME_BANNER_MAX_SCALE,
              fitScale(
                hostBox.width - 2 * HOME_BANNER_FIT_GUTTER_X,
                availableHeight - 2 * HOME_BANNER_FIT_PADDING_Y
              )
            )
          )
        : headScale;
      setVar("--banner-ad-fit-width", `${Math.floor(unitWidth * scale * 100) / 100}px`);
      setVar("--banner-ad-fit-height", `${Math.floor(unitHeight * scale * 100) / 100}px`);
      setVar("--banner-ad-width", `${unitWidth}px`);
      setVar("--banner-ad-height", `${unitHeight}px`);
      setVar("--banner-ad-ratio", `${unitWidth} / ${unitHeight}`);
      setVar("--banner-ad-scale", scale.toFixed(4));
    };
    const resizeObserver = new window.ResizeObserver(sync);
    resizeObserver.observe(host);
    const unitObserver =
      typeof window.MutationObserver === "function" ? new window.MutationObserver(sync) : null;
    if (unitObserver) {
      unitObserver.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: DISPLAY_AD_SIZE_ATTRIBUTES,
      });
    }
    sync();
    return () => {
      resizeObserver.disconnect();
      if (unitObserver) unitObserver.disconnect();
    };
  }, [hostRef, placementKey]);
}

/**
 * Provider (network) ad slide host for the infinite-loop carousel.
 * - react-responsive-carousel renders full React clones of the first/last
 *   slide around the real items; a clone must never request an ad (hidden
 *   duplicate auction, and a same-millisecond element id would steal the
 *   real slide's render).
 * - The unit is requested only once the real slide is at least half inside
 *   the carousel viewport (no offscreen impression while sliding in).
 * - After that first display the creative stays mounted across rotations so
 *   loops do not re-auction or blank. Units with refresh_seconds keep the
 *   unmount-on-leave behavior so refresh never runs offscreen.
 */
export function CarouselProviderAd({
  network,
  placementKey,
  slideIndex,
  slideCount,
  selected,
  label,
  backgroundUrl,
}) {
  const hostRef = useRef(null);
  const [realSlot, setRealSlot] = useState(false);
  const [visible, setVisible] = useState(false);
  const [latchedDevice, setLatchedDevice] = useState(null);
  const visibleRef = useRef(false);
  visibleRef.current = visible;
  const latchedRef = useRef(null);
  latchedRef.current = latchedDevice;
  useProviderBannerFit(hostRef, placementKey);

  const unit = selectDisplayUnit(network, { device: detectDeviceClass() });
  const canPersist = !(parseInt(unit && unit.refresh_seconds, 10) > 0);

  useLayoutEffect(() => {
    const host = hostRef.current;
    const li = host && host.closest ? host.closest("li.slide") : null;
    if (!li || !li.parentNode) {
      setRealSlot(true);
      return;
    }
    const domIndex = Array.prototype.indexOf.call(li.parentNode.children, li);
    setRealSlot(domIndex === slideIndex + (slideCount > 1 ? 1 : 0));
  }, [slideIndex, slideCount]);

  useLayoutEffect(() => {
    // Registered before DisplayAd's passive resize listener, so a device
    // change while offscreen releases the unit before DisplayAd re-inits it.
    function onDeviceChange() {
      if (
        latchedRef.current != null &&
        !visibleRef.current &&
        detectDeviceClass() !== latchedRef.current
      ) {
        setLatchedDevice(null);
      }
    }
    window.addEventListener("orientationchange", onDeviceChange);
    window.addEventListener("resize", onDeviceChange);
    return () => {
      window.removeEventListener("orientationchange", onDeviceChange);
      window.removeEventListener("resize", onDeviceChange);
    };
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!realSlot || !host) return undefined;
    if (typeof window.IntersectionObserver !== "function") {
      setVisible(true);
      return undefined;
    }
    const observer = new window.IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        setVisible(!!entry && entry.intersectionRatio >= 0.5);
      },
      { threshold: [0, 0.5, 1] }
    );
    observer.observe(host);
    return () => observer.disconnect();
  }, [realSlot]);

  const shown = realSlot && selected && visible;

  useEffect(() => {
    if (shown && canPersist && latchedDevice == null) {
      setLatchedDevice(detectDeviceClass());
    }
  }, [shown, canPersist, latchedDevice]);

  const active = realSlot && (shown || (canPersist && latchedDevice != null));

  return (
    <div
      ref={hostRef}
      className={
        "banner-home relative banner-home--provider" +
        (backgroundUrl ? " ad-placement--has-background" : "")
      }
    >
      {backgroundUrl ? (
        <div
          className="ad-placement__background"
          aria-hidden="true"
          style={{ backgroundImage: `url("${backgroundUrl}")` }}
        />
      ) : null}
      {backgroundUrl ? (
        <div className="ad-placement__background-overlay" aria-hidden="true" />
      ) : null}
      <div className="banner-provider-group">
        <span className="banner-ad-label">{label || "Advertisement"}</span>
        <DisplayAd
          network={network}
          placementKey={placementKey}
          className="banner-provider-display"
          active={active}
        />
      </div>
    </div>
  );
}

class HomePageBanner extends Helper {
  state = {
    wishlistApiCall: false,
    wishlistResponse: null,
    inputData: {},
    redirect: false,
    redirectPPV: false,
    redirectPaymentOption: false,
    videoDetailsFirst: null,
    playButtonClicked: false,
    wishlistStatusCheck: 0,
    selectedSlide: 0,
    adItems: [],
    adImpressionIds: {},
  };

  componentDidMount() {
    this.setState({ playButtonClicked: false });
    let wishlistStatusCheck = 0;
    if (this.props.banner && this.props.banner.wishlist_status == 1) {
      wishlistStatusCheck = 1;
    }
    this.setState({ wishlistStatusCheck });
    this.loadAdSlot();
  }

  componentDidUpdate(prevProps) {
    const prevKey = this.resolvePlacementKey(prevProps.banner);
    const nextKey = this.resolvePlacementKey(this.props.banner);
    const prevScope = JSON.stringify(
      (prevProps.banner && prevProps.banner.banner_scope) || null
    );
    const nextScope = JSON.stringify(
      (this.props.banner && this.props.banner.banner_scope) || null
    );
    if (prevKey !== nextKey || prevScope !== nextScope) {
      this.setState({ adItems: [], adImpressionIds: {} }, () => {
        this.loadAdSlot();
      });
    } else if (this.needsAdSlot(this.props.banner) && !this.needsAdSlot(prevProps.banner)) {
      this.loadAdSlot();
    }
  }

  /**
   * Placement inventory (adItems) is read only by the legacy no-order carousel
   * and by Admin-ordered "advertisement" / "ad" entries; "provider" entries
   * carry their own item inside carousel_order. Skip the request otherwise.
   */
  needsAdSlot = (banner) => {
    const b = banner || this.props.banner || {};
    const order = Array.isArray(b.carousel_order) ? b.carousel_order : [];
    if (!order.length) return true;
    return order.some(
      (entry) => !!entry && (entry.type === "advertisement" || entry.type === "ad")
    );
  };

  /**
   * Page Banner Management — placement comes from banner.placement_key.
   * Default home_banner_slot. Never accept guide_top_banner (Guide isolation).
   */
  resolvePlacementKey = (banner) => {
    const b = banner || this.props.banner || {};
    const key = String(b.placement_key || "home_banner_slot").trim();
    if (!key || key === "guide_top_banner") {
      return "home_banner_slot";
    }
    return key;
  };

  resolvePlacementContext = (banner) => {
    const b = banner || this.props.banner || {};
    const scope = b.banner_scope || {};
    const ctx = {};
    if (
      String(scope.type || "").toUpperCase() === "CATEGORY" &&
      scope.id != null &&
      scope.id !== ""
    ) {
      ctx.category_id = scope.id;
      // Also send categories[] so AdPlacementService targeting intersects correctly.
      ctx.categories = [scope.id];
    }
    return ctx;
  };

  loadAdSlot = async () => {
    if (!this.needsAdSlot()) return;
    try {
      const placementKey = this.resolvePlacementKey();
      const context = this.resolvePlacementContext();
      // Honor placement max_slots from catalog (default 1) — no hard-coded inventory count.
      const result = await fetchPlacement(placementKey, context);
      const items = (result && result.items) || [];
      this.setState({ adItems: items }, () => {
        this.trackVisibleAdImpression(this.state.selectedSlide);
      });
    } catch (e) {
      // non-fatal — content slides still render
    }
  };

  /**
   * Impression when an ad slide is actually shown (not all inventory on mount).
   * Uses built carousel slides so Home Banner Management interleave is honored.
   */
  trackVisibleAdImpression = (slideIndex) => {
    const slides = this.buildCarouselSlides();
    const slide = slides[slideIndex];
    if (!slide || !slide._ad || !slide._adItem) {
      return;
    }
    const item = slide._adItem;
    if (item && item.provider_kind === "network" && item.network) {
      // DisplayAd owns network request/load/impression tracking via adapter path.
      return;
    }
    if (!item || !item.campaign_id) {
      return;
    }
    const trackedKey = String(item.campaign_id) + ":" + String(item.creative_id || 0);
    if (this.state.adImpressionIds[trackedKey]) {
      return;
    }
    // Optimistic mark so rapid carousel ticks do not double-fire.
    this.setState((prev) => ({
      adImpressionIds: {
        ...prev.adImpressionIds,
        [trackedKey]: "pending",
      },
    }));
    trackAdImpression({
      campaign_id: item.campaign_id,
      creative_id: item.creative_id,
      placement_key: this.resolvePlacementKey(),
    }).then((res) => {
      if (res && res.impression_id) {
        this.setState((prev) => ({
          adImpressionIds: {
            ...prev.adImpressionIds,
            [trackedKey]: res.impression_id,
            [item.campaign_id]: res.impression_id,
          },
        }));
      }
    });
  };

  handleCarouselChange = (index) => {
    this.setState({ selectedSlide: index });
    this.trackVisibleAdImpression(index);
  };

  handleAdClick = async (item, event) => {
    const creative = (item && item.creative) || {};
    const url = creative.click_url;
    const trackedKey =
      String(item.campaign_id) + ":" + String(item.creative_id || 0);
    // Must run before any await: the link's default action is decided synchronously.
    if (!url) {
      event.preventDefault();
    }
    await trackAdClick({
      campaign_id: item.campaign_id,
      creative_id: item.creative_id,
      placement_key: this.resolvePlacementKey(),
      impression_id:
        this.state.adImpressionIds[trackedKey] &&
        this.state.adImpressionIds[trackedKey] !== "pending"
          ? this.state.adImpressionIds[trackedKey]
          : this.state.adImpressionIds[item.campaign_id] || null,
    });
  };

  handleWishList = (event, admin_video_id) => {
    event.preventDefault();
    let inputData = {
      ...this.state.inputData,
      admin_video_id: admin_video_id,
    };

    api.postMethod("wishlists/operations", inputData).then((response) => {
      if (response.data.success === true) {
        ToastDemo(this.props.toastManager, response.data.message, "success");
        this.setState({
          wishlistResponse: response.data,
          wishlistApiCall: true,
        });
        if (response.data.wishlist_id != null) {
          this.setState({
            wishlistStatusCheck: 1,
          });
        } else {
          this.setState({
            wishlistStatusCheck: 0,
          });
        }
      } else {
        ToastDemo(
          this.props.toastManager,
          response.data.error_messages,
          "error"
        );
      }
    });
  };

  handlePlayVideo = async (event, admin_video_id) => {
    event.preventDefault();

    this.setState({ playButtonClicked: true });
    let inputData = {
      ...this.state.inputData,
      admin_video_id: admin_video_id,
    };

    var details = await this.onlySingleVideoFirst(inputData);
    this.redirectStatus(details);
  };

  /**
   * Banner videos are playlist members, not terminal slides: when a clip ends
   * we hand the rotation back to the carousel.
   */
  advanceFromSlide = (index, total) => {
    if (total < 2 || this.state.selectedSlide !== index) {
      return;
    }
    this.setState({ selectedSlide: (index + 1) % total });
  };

  renderHeroMedia = (video, index, total) => {
    const hero = resolveHomeBannerHero(video);
    if (hero.type === "youtube") {
      const embed = youtubeEmbedUrl(hero.url);
      if (embed) {
        return (
          <iframe
            className="banner_right_video banner_right_embed"
            src={embed}
            title={video.title || "Banner video"}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        );
      }
    }
    if (hero.type === "vimeo") {
      const embed = vimeoEmbedUrl(hero.url);
      if (embed) {
        return (
          <iframe
            className="banner_right_video banner_right_embed"
            src={embed}
            title={video.title || "Banner video"}
            allow="autoplay; encrypted-media; picture-in-picture"
            allowFullScreen
          />
        );
      }
    }
    if (hero.type === "video" || hero.type === "hls") {
      return (
        <video
          className="banner_right_video"
          src={hero.url}
          poster={hero.poster || undefined}
          autoPlay
          muted
          playsInline
          preload="auto"
          onEnded={() => this.advanceFromSlide(index, total)}
          onError={(e) => {
            // Fall back to still if the clip fails to load
            const el = e && e.currentTarget;
            if (el) {
              el.style.display = "none";
              const img =
                el.parentNode &&
                el.parentNode.querySelector(".banner_right_img");
              if (img) {
                img.style.visibility = "visible";
              }
            }
          }}
        />
      );
    }
    return null;
  };

  /**
   * Build carousel slides. When Admin Home Banner Management has a persisted
   * carousel_order, interleave editorial videos + eligible ads in that order.
   * Otherwise preserve legacy behavior: content slides then ad slides.
   * Campaign scheduling remains authoritative (ineligible ads simply skip).
   * Provider sources mount DisplayAd via registry adapters (no hard-coded network).
   */
  buildCarouselSlides = () => {
    const { banner } = this.props;
    const contentSlides =
      (banner && Array.isArray(banner.data) && banner.data) || [];
    const order =
      (banner && Array.isArray(banner.carousel_order) && banner.carousel_order) ||
      [];
    const adItems = this.state.adItems || [];

    const toAdSlide = (item, idx) => ({
      _ad: true,
      _adItem: item,
      _provider: !!(item && item.provider_kind === "network" && item.network),
      admin_video_id: item && item.campaign_id
        ? `ad-${item.campaign_id}-${idx}`
        : `ad-provider-${(item && item.banner_item_id) || idx}-${idx}`,
      title:
        (item.creative && item.creative.headline) ||
        item.advertiser_name ||
        "Sponsored",
      description: (item.creative && item.creative.body) || "",
      banner_image:
        (item.creative &&
          (item.creative.image_url || item.creative.mobile_image_url)) ||
        "",
      default_image: "",
      mobile_banner_image: "",
      label: item.label || "Sponsored",
      click_url: (item.creative && item.creative.click_url) || "",
      cta_label: (item.creative && item.creative.cta_label) || "Learn more",
    });

    const videoById = {};
    contentSlides.forEach((video) => {
      const id = Number(
        video && (video.admin_video_id != null ? video.admin_video_id : video.id)
      );
      if (id > 0) {
        videoById[id] = video;
      }
    });
    const adByCampaignId = {};
    adItems.forEach((item, idx) => {
      if (item && item.campaign_id) {
        adByCampaignId[Number(item.campaign_id)] = { item, idx };
      }
    });

    if (!order.length) {
      return contentSlides.concat(
        adItems
          .filter(
            (item) =>
              item &&
              (item.campaign_id ||
                (item.provider_kind === "network" &&
                  item.network &&
                  isDisplayNetworkItem(item.network)))
          )
          .map((item, idx) => toAdSlide(item, idx))
      );
    }

    const slides = [];
    const usedVideos = {};
    const usedAds = {};
    const usedProviders = {};
    order.forEach((entry) => {
      if (!entry || !entry.type) {
        return;
      }
      const id = Number(entry.id);
      if (entry.type === "video" || entry.type === "editorial") {
        if (videoById[id] && !usedVideos[id]) {
          slides.push(videoById[id]);
          usedVideos[id] = true;
        }
        return;
      }
      if (entry.type === "provider") {
        const item =
          entry.item &&
          entry.item.network &&
          isDisplayNetworkItem(entry.item.network)
            ? entry.item
            : null;
        if (item && !usedProviders[id]) {
          slides.push(toAdSlide(item, id));
          usedProviders[id] = true;
        }
        return;
      }
      if (entry.type === "advertisement" || entry.type === "ad") {
        const found = adByCampaignId[id];
        if (found && !usedAds[id]) {
          slides.push(toAdSlide(found.item, found.idx));
          usedAds[id] = true;
        }
      }
    });

    // Videos not yet in the manager order still appear (append).
    contentSlides.forEach((video) => {
      const id = Number(
        video && (video.admin_video_id != null ? video.admin_video_id : video.id)
      );
      if (id > 0 && !usedVideos[id]) {
        slides.push(video);
        usedVideos[id] = true;
      }
    });

    // When manager order exists, ads not listed are omitted (SSOT). Manager
    // inactive rows are already absent from carousel_order.

    return slides;
  };

  render() {
    const { t } = this.props;
    const slides = this.buildCarouselSlides();

    if (this.state.playButtonClicked) {
      const returnToVideo = this.renderRedirectPage(
        this.state.videoDetailsFirst
      );

      if (returnToVideo != null) {
        return returnToVideo;
      }
    }
    const { wishlistStatusCheck } = this.state;
    const slideCount = slides.length;

    return (
      <section className="banner-slider slider">
        <Carousel
          showThumbs={false}
          infiniteLoop={slideCount > 1}
          showStatus={false}
          showArrows={true}
          showIndicators={false}
          autoPlay={true}
          interval={
            Number(configuration.get("configData.home_banner_time_interval")) ||
            3000
          }
          stopOnHover={true}
          swipeable={true}
          selectedItem={this.state.selectedSlide}
          onChange={this.handleCarouselChange}
        >
          {slides.map((video, slideIndex) => {
            if (video._ad) {
              const item = video._adItem;
              if (video._provider && item && item.network) {
                return (
                  <div
                    className="banner-sec banner-sec--ad banner-sec--provider"
                    key={video.admin_video_id}
                  >
                    <div className="row m-0">
                      <div className="col-12 col-md-12 col-lg-12 col-xl-12 p-0">
                        <CarouselProviderAd
                          network={item.network}
                          placementKey={this.resolvePlacementKey()}
                          slideIndex={slideIndex}
                          slideCount={slideCount}
                          selected={slideIndex === this.state.selectedSlide}
                          label={video.label || "Advertisement"}
                          backgroundUrl={homeBannerBackgroundUrl(
                            this.props.banner,
                            this.resolvePlacementKey()
                          )}
                        />
                      </div>
                    </div>
                  </div>
                );
              }
              const poster = resolveMediaUrl(video.banner_image);
              const href = video.click_url || "#";
              const external = href.indexOf("http") === 0;
              return (
                <div className="banner-sec banner-sec--ad" key={video.admin_video_id}>
                  <div className="row m-0">
                    <div className="col-12 col-md-12 col-lg-12 col-xl-12 p-0">
                      <div className="banner-home relative">
                        {poster ? (
                          <img
                            className="banner_right_img"
                            src={poster}
                            alt={video.title || "Sponsored"}
                            onError={handleMediaImgError}
                          />
                        ) : (
                          <div className="banner_right_img banner_right_img--ad-fallback" />
                        )}
                        <div className="banner_right_overlay" />
                      </div>
                    </div>
                  </div>
                  <div className="banner-content">
                    <div className="banner-text-centeralign">
                      <div>
                        <span className="banner-ad-label">{video.label}</span>
                        <h1 className="overview_video_title">{video.title}</h1>
                        {video.description ? (
                          <h4 className="banner_video_text">{video.description}</h4>
                        ) : null}
                        <div className="banner-btn-sec">
                          <a
                            href={href}
                            className="btn btn-white"
                            target={external ? "_blank" : undefined}
                            rel={external ? "noopener noreferrer" : undefined}
                            onClick={(event) => this.handleAdClick(item, event)}
                          >
                            {video.cta_label}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            }

            const hero = resolveHomeBannerHero(video);
            const showVideo =
              hero.type === "video" ||
              hero.type === "hls" ||
              hero.type === "youtube" ||
              hero.type === "vimeo";
            return (
              <div
                className={
                  "banner-sec" + (showVideo ? " banner-sec--video" : "")
                }
                key={video.admin_video_id}
              >
                <div className="row m-0">
                  <div className="col-12 col-md-12 col-lg-12 col-xl-12 p-0">
                    <div className="banner-home relative">
                      {showVideo
                        ? this.renderHeroMedia(video, slideIndex, slideCount)
                        : null}
                      {/*
                        Keep the still in document flow when video plays so the
                        hero retains height (video/iframe are position:absolute).
                        visibility:hidden — not display:none — preserves layout.
                      */}
                      <img
                        className={
                          "banner_right_img" +
                          (showVideo ? " banner_right_img--under-video" : "")
                        }
                        style={
                          showVideo ? { visibility: "hidden" } : undefined
                        }
                        src={hero.poster}
                        alt="banner img"
                        onError={handleMediaImgError}
                      />
                      <div className="banner_right_overlay" />
                    </div>
                  </div>
                </div>
                <div className="banner-content">
                  <div className="banner-text-centeralign">
                    <div>
                      <h1 className="overview_video_title">{video.title}</h1>
                      <h4 className="banner_video_text">{video.description}</h4>
                      <div className="banner-btn-sec">
                        <Link
                          to="#"
                          onClick={(event) =>
                            this.handlePlayVideo(event, video.admin_video_id)
                          }
                          className="btn btn-white"
                        >
                          <i className="fas fa-play mr-2" />
                          {t("play")}
                        </Link>
                        <Link
                          to="#"
                          className={
                            "btn btn-grey" +
                            (wishlistStatusCheck == 1
                              ? " banner-collection-selected"
                              : "")
                          }
                          onClick={(event) =>
                            this.handleWishList(event, video.admin_video_id)
                          }
                          value={video.admin_video_id}
                          aria-label={
                            wishlistStatusCheck == 1
                              ? "In " + BrandAssetManager.collectionLabel()
                              : "Add to " + BrandAssetManager.collectionLabel()
                          }
                          aria-pressed={wishlistStatusCheck == 1 ? "true" : "false"}
                        >
                          {wishlistStatusCheck == 1 ? (
                            <div className="banner-collection-cta is-selected">
                              <img
                                src={window.location.origin + "/images/tick.png"}
                                className="mr-2 banner-wishlist-icon"
                                alt=""
                              />
                              {BrandAssetManager.collectionLabel()}
                            </div>
                          ) : (
                            <div className="banner-collection-cta">
                              <img
                                src={window.location.origin + "/images/add.png"}
                                className="mr-2 banner-wishlist-icon"
                                alt=""
                              />
                              {BrandAssetManager.collectionLabel()}
                            </div>
                          )}
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="banner-action-right">
                  <div
                    className="banner-action-sec"
                    style={{ visibility: "hidden" }}
                  >
                    <div className="audio-action-icons">
                      <i className="material-icons playlist-icon-1">volume_up</i>
                    </div>
                    <div className="rating-count-sec">
                      <h4>{video.age}+</h4>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </Carousel>
      </section>
    );
  }
}

export default withToastManager(translate(HomePageBanner));
